import { useContext, useState, useEffect } from 'react';
import AuthContext from '../../store/auth-context';
import classes from "./Profile.module.scss";
import { updateUserImage, updateProfile, refreshUserVerification } from '../../services';
import { useDisclosure, useToast } from '@chakra-ui/react';
import UpgradeModal from '../UI/UpgradeModal';

// The API stores the address as { line1, line2, city, state, postalCode,
// country } (Backend/app/models/Users.js). It used to be one line of text, so
// accept either shape: rendering the object directly blanks the page.
const addressParts = (address) => {
  if (!address) return {};
  return typeof address === 'string' ? { line1: address } : address;
};

// "123 Main St, Vienna, VA 22180" - empty when only the default country is set.
const formatAddress = (address) => {
  const a = addressParts(address);
  const cityLine = [a.city, [a.state, a.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [a.line1, a.line2, cityLine].filter(Boolean).join(', ');
};

// The form's fields as they would be filled from the saved profile.
const formFieldsFrom = (userData) => {
  const a = addressParts(userData.address);
  return {
    address: a.line1 || '',
    city: a.city || userData.city || '',
    state: a.state || userData.state || '',
    zipcode: a.postalCode || userData.zipcode || '',
  };
};

const ProfileDetails = () => {
  const { userData, isFreeTier } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState(null); // To store the uploaded image
  const [previewImage, setPreviewImage] = useState(null); // For previewing the selected image
  // The isUploading state is declared but not used in this component currently
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    port: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    image: ''
  });

  // Add function to refresh verification status
  const handleRefreshVerificationStatus = async () => {
    setIsRefreshing(true);
    try {
      const updatedUserData = await refreshUserVerification();
      if (updatedUserData) {
        if (updatedUserData.isVerified) {
          toast({
            title: "Account verified!",
            description: "Your account has been verified as VIP. You may need to log out and log back in to see all VIP features.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Still on Free Tier",
            description: "Your account is still on the Free tier. If you've recently made a payment, please allow some time for processing.",
            status: "info",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error("Error refreshing verification status:", error);
      toast({
        title: "Error",
        description: "Unable to refresh verification status. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFileChange = (e) => {
    // The isUploading state is declared but not used in this component currently
    const file = e.target.files[0];

    if (file) {
      // POST /users/upload only accepts images and rejects anything over 5MB, so
      // catch it here rather than letting the upload come back as a 400.
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Unsupported file",
          description: "Please choose an image file.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Profile images must be 5MB or smaller.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      setProfileImage(file); // Save the selected file
      setPreviewImage(URL.createObjectURL(file)); // Generate a preview URL
    }
  };

const handleImageUpload = async () => {
    
    // The isUploading state is declared but not used in this component currently
    const formData = new FormData();
    formData.append("profileImage", profileImage);  // 'profileImage' should match the name used in the backend
  
    try {
        const res = await updateUserImage(formData); // Wait for the API response
        console.log("Response: ", res);

        if (res?.message === "Profile image updated successfully!") {
            setFormData(prev => ({
            ...prev,
            image: res.imageUrl
        }));
        setIsEditing(false);
        //console.log("User updated");
        } else {
            console.log("User not updated");
            toast({
              title: "Upload failed",
              description: res?.message || "Could not upload your profile image. Please try again.",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
        }
    } catch (error) {
        console.error("Image upload failed:", error);
        toast({
          title: "Upload failed",
          description: "Could not upload your profile image. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
    }
}

  useEffect(() => {
    if (userData) {
      setFormData({
        name: `${userData.firstName} ${userData.lastName}` || '',
        port: userData.port || '',
        email: userData.email || '',
        ...formFieldsFrom(userData),
        phone: userData.phone || '',
        image: userData.image || ''
      });
    }
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  // This function is defined for future use but not currently used
  // eslint-disable-next-line no-unused-vars
  const triggerFileUpload = () => {
    document.getElementById("imageFileInput").click();
  }

  const handleSaveProfile = () => {
    if (profileImage) {
      handleImageUpload();
    }
    const saved = formFieldsFrom(userData);
    if (
      formData.name !== `${userData.firstName} ${userData.lastName}` ||
      formData.port !== (userData.port || '') ||
      formData.address !== saved.address ||
      formData.city !== saved.city ||
      formData.state !== saved.state ||
      formData.zipcode !== saved.zipcode
    ) {
      // Only fields the API accepts: it rejects unknown keys (city, state and
      // zipcode used to be sent loose and failed every save) and empty strings
      // for firstName, lastName and port.
      const name = formData.name.trim();
      const space = name.indexOf(' ');
      const firstName = space === -1 ? name : name.slice(0, space);
      const lastName = space === -1 ? '' : name.slice(space + 1).trim();
      const payload = {
        address: {
          line1: formData.address.trim(),
          city: formData.city.trim(),
          state: formData.state.trim(),
          postalCode: formData.zipcode.trim(),
        },
      };
      if (firstName) payload.firstName = firstName;
      if (lastName) payload.lastName = lastName;
      if (formData.port) payload.port = formData.port;
      updateProfile(payload).then((res) => {
        if (res?.message === "User updated succcessfully!") {
          console.log("User updated");
          toast({
            title: "Profile updated",
            description: "Your profile has been updated successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          setIsEditing(false);
        } else {
          console.log("User not updated");
          toast({
            title: "Update failed",
            description: "There was an error updating your profile. Please try again.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      }).catch((error) => {
        console.error("Failed to update profile:", error);
        toast({
          title: "Update failed",
          description: "There was an error updating your profile. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });
    } else {
      setIsEditing(false);
    }
  };

  // Handle profile picture click to upload new image
  const handleProfilePictureClick = () => {
    document.getElementById("imageFileInput").click();
  };

  // Handle saving just the profile picture
  const handleSaveProfilePicture = async () => {
    if (profileImage) {
      try {
        await handleImageUpload();
        setProfileImage(null);
        setPreviewImage(null);
        toast({
          title: "Profile picture updated",
          description: "Your profile picture has been updated successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Failed to update profile picture:", error);
        toast({
          title: "Update failed",
          description: "There was an error updating your profile picture. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleCancelPictureChange = () => {
    setProfileImage(null);
    setPreviewImage(null);
  };

  // Format deposit amount with a dollar sign and two decimal places
  const formatDepositAmount = (amount) => {
    if (!amount) return '$0.00';
    return '$' + parseFloat(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Calculate bidding limit (10x deposit)
  const calculateBiddingLimit = (deposit) => {
    if (!deposit) return '$0.00';
    const limit = parseFloat(deposit) * 10;
    return '$' + limit.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  useEffect(() => {
    // Add a subtle fade-in animation for the form when editing
    if (isEditing) {
      const formFields = document.querySelectorAll(`.${classes.formInput}`);
      formFields.forEach((field, index) => {
        setTimeout(() => {
          field.classList.add('animate__animated', 'animate__fadeIn');
        }, index * 50);
      });
    }
  }, [isEditing]);

  return (
    <div className={classes.profileWrapper}>
      {/* Profile Section */}
      <div className={classes.accountCard}>
        <div className={classes.profileHeader}>
          <div className={`${classes.profilePic} ${isEditing ? classes.editMode : ''}`} onClick={isEditing ? handleProfilePictureClick : undefined}>
            <img 
              src={previewImage || formData.image || "https://mmpubimages.s3.us-west-1.amazonaws.com/defaultprofile.png"}
              alt="profile pic"
              className={classes.profilePicImg}
              onError={(e) => {
                console.log("Error loading profile image:", e);
                e.target.src = "https://mmpubimages.s3.us-west-1.amazonaws.com/defaultprofile.png";
              }}
            />
            {isEditing && (
              <div className={classes.profilePicOverlay}>
                <i className="fas fa-camera"></i>
                <span>Edit Photo</span>
              </div>
            )}
          </div>
          <div className={classes.profileText}>
            <div>
              <h2>Profile</h2>
              <p>Update your photo and personal details.</p>
            </div>
            <button className={classes.editButton} onClick={() => setIsEditing(true)}>
              Edit <i className="fas fa-pencil-alt"></i>
            </button>
          </div>
        </div>
        
        {/* Profile Picture Action Buttons */}
        {profileImage && (
          <div className={classes.profilePictureActions}>
            <button 
              className={classes.formButtonSecondary} 
              onClick={handleCancelPictureChange}
            >
              Cancel
            </button>
            <button 
              className={classes.formButtonPrimary} 
              onClick={handleSaveProfilePicture}
            >
              Save Photo
            </button>
          </div>
        )}
        
        {/* Hidden file input */}
        <input 
          type="file" 
          id="imageFileInput" 
          onChange={handleFileChange} 
          accept="image/*"
          style={{ display: 'none' }}
        />
      </div>
      
      {/* Account Tier Section */}
      <div className={classes.accountCard}>
        <div className={classes.accountTierHeader}>
          <h3>Account Tier</h3>
          <div className={`${classes.vipBadge} ${isFreeTier ? classes.freeBadge : ''}`}>
            {isFreeTier ? 'Free' : 'VIP'} {!isFreeTier && <i className="fas fa-crown"></i>}
          </div>
        </div>
        
        {/* Deposit Info */}
        <div className={classes.depositInfo}>
          <div className={classes.infoItem}>
            <div className={classes.label}>Current Deposit:</div>
            <div className={classes.amount}>{formatDepositAmount(userData?.deposit?.amount)}</div>
          </div>
          <div className={classes.infoItem}>
            <div className={classes.label}>Bidding Limit:</div>
            <div className={classes.amount}>
              {calculateBiddingLimit(userData?.deposit?.amount)}
              <span className={classes.limitNote}>(10x your deposit)</span>
            </div>
          </div>
        </div>
        
        {isFreeTier ? (
          <>
            <div className={classes.freeTierSection}>
              <p className={classes.freeTierInfo}>
                You're currently on the Free Tier. Upgrade to VIP to unlock all features:
              </p>
              <ul className={classes.tierFeaturesList}>
                <li>Access to auction listings</li>
                <li>View all vehicle photos</li>
                <li>See exact auction times</li>
                <li>Use price estimator</li>
                <li>Request inspections</li>
                <li>Place bids on vehicles</li>
              </ul>
              <div className={classes.upgradeActions}>
                <button 
                  className={classes.upgradeButton}
                  onClick={onOpen}
                >
                  <i className="fas fa-level-up-alt"></i> Upgrade to VIP
                </button>
                <button
                  className={classes.refreshButton}
                  onClick={handleRefreshVerificationStatus}
                  disabled={isRefreshing}
                >
                  <i className="fas fa-sync-alt"></i> {isRefreshing ? 'Refreshing...' : 'Refresh Tier Status'}
                </button>
              </div>
              <p className={classes.refreshNote}>
                Already placed a deposit? Click "Refresh Tier Status" to update your account.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className={classes.bidNote}>
              Need a higher bidding limit? Make an additional deposit to increase your limit.
            </div>
            
            <button 
              className={classes.depositButton}
              onClick={onOpen}
              style={{
                backgroundColor: "#7962e8",
                color: "white",
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "30px",
                padding: "12px 20px",
                border: "none",
                cursor: "pointer",
                fontWeight: "600"
              }}
            >
              <span 
                style={{
                  backgroundColor: "white",
                  color: "#7962e8",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center", 
                  justifyContent: "center",
                  marginRight: "10px",
                  fontSize: "14px"
                }}
              >+</span> 
              Make Additional Deposit
            </button>
          </>
        )}
      </div>
      
      {/* User Information */}
      <div className={classes.accountCard}>
        <h3 className={classes.cardTitle}>Personal Information</h3>
        <div className={classes.userInfoGrid}>
          <div className={classes.infoItem}>
            <div className={classes.label}>Name</div>
            <div className={classes.value}>{userData?.firstName} {userData?.lastName}</div>
          </div>
          
          <div className={classes.infoItem}>
            <div className={classes.label}>Port</div>
            <div className={classes.value}>{userData?.port || "Brunswick"}</div>
          </div>
          
          <div className={classes.infoItem}>
            <div className={classes.label}>Email</div>
            <div className={classes.value}>{userData?.email}</div>
          </div>
          
          {formatAddress(userData?.address) && (
            <div className={classes.infoItem}>
              <div className={classes.label}>Address</div>
              <div className={classes.value}>{formatAddress(userData?.address)}</div>
            </div>
          )}
          
          {userData?.city && (
            <div className={classes.infoItem}>
              <div className={classes.label}>City</div>
              <div className={classes.value}>{userData?.city}</div>
            </div>
          )}
          
          {userData?.state && (
            <div className={classes.infoItem}>
              <div className={classes.label}>State</div>
              <div className={classes.value}>{userData?.state}</div>
            </div>
          )}
          
          {userData?.zipcode && (
            <div className={classes.infoItem}>
              <div className={classes.label}>Zipcode</div>
              <div className={classes.value}>{userData?.zipcode}</div>
            </div>
          )}
          
          {userData?.phone && (
            <div className={classes.infoItem}>
              <div className={classes.label}>Phone</div>
              <div className={classes.value}>{userData?.phone}</div>
            </div>
          )}
        </div>
        
        {isEditing ? (
          <>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Name</label>
              <input
                className={classes.formInput}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Port</label>
              <select 
                className={classes.formInput}
                name="port"
                value={formData.port}
                onChange={handleInputChange}
              >
                <option className={classes.selectOption}>Select Port</option>
                <option className={classes.selectOption} value="Baltimore">Baltimore</option>
                <option className={classes.selectOption} value="Brunswick">Brunswick</option>
                <option className={classes.selectOption} value="Freeport (Texas)">Freeport (Texas)</option>
                <option className={classes.selectOption} value="Galveston">Galveston</option>
                <option className={classes.selectOption} value="Guam">Guam</option>
                <option className={classes.selectOption} value="Honolulu">Honolulu</option>
                <option className={classes.selectOption} value="Jacksonville">Jacksonville</option>
                <option className={classes.selectOption} value="Long Beach">Long Beach</option>
                <option className={classes.selectOption} value="Los Angeles">Los Angeles</option>
                <option className={classes.selectOption} value="New York">New York</option>
                <option className={classes.selectOption} value="Newport News">Newport News</option>
                <option className={classes.selectOption} value="Port Hueneme">Port Hueneme</option>
                <option className={classes.selectOption} value="Saipan">Saipan</option>
                <option className={classes.selectOption} value="Tacoma">Tacoma</option>
                <option className={classes.selectOption} value="Non USA please contact">Non USA please contact</option>
              </select>
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Email</label>
              <input 
                className={classes.formInput}
                name="email"
                value={formData.email}
                disabled={true}
              />
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Address</label>
              <input
                className={classes.formInput}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>
            <div className={classes.formGroup}>
              <div className={classes.fullwidth}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input 
                    className={classes.formInput}
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                  />
                  <input 
                    className={classes.formInput}
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="State"
                  />
                  <input 
                    className={classes.formInput}
                    name="zipcode"
                    value={formData.zipcode}
                    onChange={handleInputChange}
                    placeholder="Zipcode"
                  />
                </div>
              </div>
            </div>
            <div className={classes.formGroup}>
              <label className={classes.formLabel}>Phone Number</label>
              <input 
                className={classes.formInput}
                name="phone"
                value={formData.phone}
                disabled={true}
              />
            </div>
            <div className={classes.formButtons}>
              <button className={classes.formButtonSecondary} onClick={() => setIsEditing(false)}>Cancel</button>
              <button className={classes.formButtonPrimary} onClick={handleSaveProfile}>Save</button>
            </div>
          </>
        ) : null}
        
        {isOpen && <UpgradeModal isOpen={isOpen} onClose={onClose} isAdditionalDeposit={!isFreeTier} />}
      </div>
    </div>
  );
}

export default ProfileDetails;